import { default as classnames } from "classnames";
import { FC, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { SWRMutationResponse } from "swr/mutation";
import { useOnClickOutsidePortal } from "../../hooks/useOnClickOutsidePortal";
import { useI18n } from "../../hooks/useTranslations";
import { CustomError } from "../../http/errors";
import { ColumnFilterValue, MenuOptionValue, StoredColumnFilterValue } from "../../types";
import SvgArrowDown from "../icons/ArrowDown";
import SvgArrowUp from "../icons/ArrowUp";
import SvgClose from "../icons/Close";
import Button from "./Button";
import { ColumnFilter } from "./columnFilters/types";
import styles from "./HeaderMenu.module.css";
import Icon from "./Icon";

interface HeaderMenuProps {
  show: boolean;
  anchorEl: HTMLElement | null;
  alignright?: boolean;
  title: string;
  fieldName: string;
  onClose: () => void;
  setOrder?: (order: "DESC" | "ASC") => void;
  filters?: {
    getSelected: (fieldName: string) => StoredColumnFilterValue;
    update: (fieldName: string, value: ColumnFilterValue) => void;
    get: (
      fieldName: string,
      signal: AbortSignal,
    ) => SWRMutationResponse<MenuOptionValue[] | null, CustomError, string, never>;
  };
  filter?: ColumnFilter;
  formatter?: (value: MenuOptionValue) => string | undefined;
}

const HeaderMenu: FC<HeaderMenuProps> = (props) => {
  const i18n = useI18n();
  const [isVisible, setIsVisible] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // fixed-position coords in viewport space
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  useOnClickOutsidePortal(menuRef, props.anchorEl, () => props.onClose(), isVisible);

  useEffect(() => {
    props.show !== isVisible && setIsVisible(props.show);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.show]);

  const selected = props.filters?.getSelected(props.fieldName) ?? { values: [] };
  const hasActiveFilter = selected.values.length > 0 || !!selected.operator;

  const clearFilters = () => props.filters?.update(props.fieldName, { operator: undefined, values: [] });

  const computePosition = () => {
    const anchor = props.anchorEl;
    const menu = menuRef.current;
    if (!anchor || !menu) return;

    const a = anchor.getBoundingClientRect();

    // Ensure we measure menu size after it’s in the DOM
    const menuW = menu.offsetWidth;
    const menuH = menu.offsetHeight;

    const margin = 8;

    // Default: below anchor, left aligned
    let left = a.left;
    let top = a.bottom;

    // If align right: right-align menu to anchor
    if (props.alignright) {
      left = a.right - menuW;
    }

    // Clamp horizontally to viewport
    left = Math.min(left, window.innerWidth - menuW - margin);
    left = Math.max(left, margin);

    // Flip up if not enough space below
    const spaceBelow = window.innerHeight - a.bottom;
    if (spaceBelow < menuH + margin && a.top >= menuH + margin) {
      top = a.top - menuH;
    }

    // Clamp vertically
    top = Math.min(top, window.innerHeight - menuH - margin);
    top = Math.max(top, margin);

    setPos((current) => {
      if (current?.left === left && current.top === top) {
        return current;
      }

      return { left, top };
    });
  };

  // Compute on open and whenever content size changes
  useLayoutEffect(() => {
    if (!props.show) return;
    requestAnimationFrame(computePosition);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.show, props.alignright, props.anchorEl]);

  // Recompute on scroll/resize, and whenever the menu's own content size changes (the injected
  // filter component can grow/shrink independently, e.g. a list loading in or a mode switch)
  useEffect(() => {
    if (!props.show) return;

    const onScroll = () => computePosition();
    const onResize = () => computePosition();

    window.addEventListener("scroll", onScroll, true); // capture = any ancestor scroller
    window.addEventListener("resize", onResize);

    const resizeObserver = new ResizeObserver(() => computePosition());
    if (menuRef.current) {
      resizeObserver.observe(menuRef.current);
    }

    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
      resizeObserver.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.show, props.alignright, props.anchorEl]);

  if (typeof document === "undefined" || !props.show) return null;

  const FilterComponent = props.filter;

  return createPortal(
    <div
      ref={menuRef}
      className={classnames(styles.menu, isVisible ? styles.visible : undefined)}
      style={{
        left: pos ? `${pos.left}px` : undefined,
        top: pos ? `${pos.top}px` : undefined,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div>
        {!props.setOrder && (
          <div title={i18n.t.common.columnNotSortable} className={classnames(styles.option, styles.wrap)}>
            {i18n.t.common.columnNotSortable}
          </div>
        )}

        {props.setOrder && (
          <>
            <div
              title={i18n.t.common.sortAscending}
              className={classnames(styles.option, styles["option-enabled"])}
              onClick={() => props.setOrder?.("ASC")}
            >
              <SvgArrowUp />
              <span>{i18n.t.common.ascending}</span>
            </div>
            <div
              title={i18n.t.common.sortDescending}
              className={classnames(styles.option, styles["option-enabled"])}
              onClick={() => props.setOrder?.("DESC")}
            >
              <SvgArrowDown />
              <span>{i18n.t.common.descending}</span>
            </div>
          </>
        )}

        <div>
          <hr className={styles.separator} />
        </div>

        {!props.filters && (
          <div title={i18n.t.common.columnNotFilterable} className={classnames(styles.option, styles.wrap)}>
            {i18n.t.common.columnNotFilterable}
          </div>
        )}

        {props.filters && FilterComponent && (
          <>
            <div
              title={i18n.t.common.deleteFilter(props.title)}
              className={classnames(
                styles.option,
                styles.wrap,
                hasActiveFilter ? styles["option-enabled"] : styles["option-disabled"],
              )}
              onClick={clearFilters}
            >
              <Icon glyph={SvgClose} />
              <span>{i18n.t.common.deleteFilters}</span>
            </div>

            <FilterComponent
              fieldName={props.fieldName}
              title={props.title}
              formatter={props.formatter}
              get={props.filters.get}
              selected={selected}
              onChange={(value) => props.filters?.update(props.fieldName, value)}
            />
          </>
        )}

        <div className={styles["close-button"]}>
          <Button
            buttonProps={{
              onClick: (e: any) => {
                e.stopPropagation();
                props.onClose();
              },
            }}
          >
            {i18n.t.common.close}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default HeaderMenu;
